<script lang="ts">
  import { onMount, tick } from "svelte"
  import { xhr, toast, formatDate, bullets, wait } from "$app/utils"
  import { Pen, Trash } from "lucide-svelte"

  let { book }: { book: BookType } = $props()

  book.book_readings = []
  let status = "closed"

  let loading = $state(false)
  let currentReading: BookReadingType = $state(null)

  let newReadingEl = $state(null)
  let newTitleEl = $state(null)

  async function loadBook(id: number): Promise<void> {
    try {
      book.book_readings = []
      loading = true
      const resp = await xhr.get(`/admin/books/${id}`)
      status = "loaded"
      book.book_readings = resp.data.book_readings
    } catch (e) {
      console.error(e)
      toast("There was an error loading the book", "error")
    } finally {
      loading = false
    }
  }

  async function addBookReading(): void {
    title = ""
    formStatus = "new"
    await tick()
    scrollIntoView(newReadingEl)
    newTitleEl.focus()
  }

  let title = $state("")
  let formStatus = $state("")
  async function createReading() {
    try {
      const resp = await xhr.post(`/admin/book_readings/${book.id}`, {
        book_reading: { title }
      })

      const reading = resp.data
      book.book_readings = [...book.book_readings, reading]

      formStatus = ""
    } catch (e) {
      console.error(e)
    } finally {
    }
  }

  let editTitleEl = $state(null)
  async function editReading(reading: BookReadingType): void {
    formStatus = "edit"
    title = reading.title
    currentReading = reading
    await tick()
    editTitleEl.focus()
  }

  async function updateReading() {
    try {
      const resp = await xhr.patch(
        `/admin/book_readings/${book.id}/${currentReading.id}`,
        {
          book_reading: { title }
        }
      )

      let idx = book.book_readings.findIndex((b) => b.id === resp.data.id)

      book.book_readings[idx].title = resp.data.title
      formStatus = ""
    } catch (e) {
      console.error(e)
    } finally {
    }
  }

  async function deleteReading(reading: BookReadingType) {
    reading._status = "selected"
    book.book_readings = book.book_readings
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected reading?")) {
      reading._status = ""
      book.book_readings = book.book_readings
      return
    }

    try {
      await xhr.delete(`/admin/book_readings/${book.id}/${reading.id}`)

      let idx = book.book_readings.findIndex((b) => b.id === reading.id)
      book.book_readings.splice(idx, 1)
      book.book_readings = book.book_readings
      toast("Reading was successfully deleted", "success")
    } catch (e) {
      toast("There was an error deleting the reading", "error")
      console.error(e)
    } finally {
    }
  }

  $effect(() => {
    if (mounted && open && status !== "loaded") {
      status = "open"
      loadBook(book.id)
    } else if (!open) {
      title = ""
      formStatus = ""
      status = "closed"
    }
  })

  let mounted = $state(false)
  onMount(() => {
    mounted = true
  })

  const scrollIntoView = (node) => {
    node.scrollIntoView({
      behavior: "smooth"
    })
  }
</script>

<div class="flex text-gray-700">
  <div>
    <img src={book.image_url} alt={book.title} width="220" />
  </div>

  <div class="w-full px-2">
    <div class="font-semibold text-xl text-gray-700">
      {book.title}
    </div>

    <div class="font-bold py-2 text-gray-700" title="Author">
      {book.author}
    </div>

    <div class="h-40 overflow-y-auto p-2 text-sm border border-gray-500">
      <p>
        {@html book.description}
      </p>
    </div>
  </div>
</div>

<div class="grid grid-cols-4 gap-4 items-center mt-2 text-gray-700">
  <div class="font-bold">
    {book.public ? "Public" : "Private"}
  </div>

  <div class="italic" title="Publication Date">
    {formatDate(book.publication_date)}
  </div>

  <div class="flex gap-2 items-center">
    <div class="label-color text-sm">Status:</div>
    <div>{book.status}</div>
  </div>

  <div>
    {#if book.affiliate_link}
      <a
        href={book.affiliate_link}
        target="_blank"
        class="bg-yellow-200 text-black rounded-md px-2 py-1"
        rel="noopener noreferrer"
      >
        Affiliate Link
      </a>
    {:else}
      <span class="text-gray-400">No affiliate link</span>
    {/if}
  </div>
</div>

<div class="input-cont">
  <label for="">Categories</label>
  <div class="flex gap-2">
    {@html bullets(book.categories)}
  </div>
</div>

<div class="input-cont">
  <label for="">Values</label>
  <div class="flex gap-2">
    {@html bullets(book.values)}
  </div>
</div>

<hr class="mt-4 mb-2" />
<div class="mt-4 text-gray-700">
  <div class="flex gap-2 grid grid-cols-2">
    <div>
      <h3 class="text-lg font-semibold">Book Reading Assignments</h3>
    </div>
    <div class="flex justify-end">
      {#if loading}
        <div class="text-gray-400">Loading...</div>
      {:else}
        <span class="text-gray-400">
          {book.book_readings.length ? book.book_readings.length : "No Book"} readings
        </span>
      {/if}
    </div>
  </div>

  <div class="py-2">
    <ul
      class="border border-gray-300 rounded max-h-128 overflow-y-auto py-1 px-4"
    >
      {#each book.book_readings.sort((a, b) => a.position - b.position) as reading}
        <li
          class="flex gap-4 py-2"
          class:bg-gray-600={reading._status === "selected"}
        >
          {#if formStatus === "edit" && currentReading.id === reading.id}
            <div>
              <input
                type="text"
                class="input-admin"
                bind:value={title}
                bind:this={editTitleEl}
              />
              <button
                class="btn primary"
                onclick={() => updateReading()}
                type="button"
              >
                Update
              </button>
              <button
                class="btn secondary"
                onclick={() => (formStatus = "")}
                type="button"
              >
                Cancel
              </button>
            </div>
          {:else}
            <div class="grow">{reading.title}</div>
            <button onclick={() => editReading(reading)} type="button">
              <Pen /></button
            >
            <button onclick={() => deleteReading(reading)} type="button">
              <Trash />
            </button>
          {/if}
        </li>
      {/each}

      {#if formStatus === "new"}
        <li class="flex gap-4 py-2" bind:this={newReadingEl}>
          <input
            type="text"
            class="input-admin"
            bind:value={title}
            bind:this={newTitleEl}
            placeholder="Reading title"
          />
          <button
            class="btn primary"
            onclick={() => createReading()}
            type="button"
          >
            Create
          </button>
          <button
            class="btn secondary"
            onclick={() => (formStatus = "")}
            type="button"
          >
            Cancel
          </button>
        </li>
      {/if}
    </ul>
    <div class="mt-2">
      {#if formStatus === ""}
        <button onclick={addBookReading} class="btn primary">
          Add reading assignment
        </button>
      {/if}
    </div>
  </div>
</div>
